import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Loader2, Search, Send, Clock, Eye, CheckCircle, XCircle, X,
  AlertTriangle, ShieldAlert, Save
} from 'lucide-react';
import { AttachmentUploader, AttachmentGallery } from './AttachmentUploader';
import { ExportListButtons } from './ExportListButtons';
import { ImageWithFallback } from './ui/image-with-fallback';

const STATUS_STYLE = {
  open:      { label: 'Open',      bg: 'bg-amber-500/10',  text: 'text-amber-400',  icon: Clock,       border: 'border-amber-500/20' },
  in_review: { label: 'In Review', bg: 'bg-[#00E5FF]/10',  text: 'text-[#00E5FF]',  icon: Eye,         border: 'border-[#00E5FF]/20' },
  resolved:  { label: 'Resolved',  bg: 'bg-[#00E676]/10',  text: 'text-[#00E676]',  icon: CheckCircle, border: 'border-[#00E676]/20' },
  rejected:  { label: 'Rejected',  bg: 'bg-[#FF1744]/10',  text: 'text-[#FF1744]',  icon: XCircle,     border: 'border-[#FF1744]/20' },
  closed:    { label: 'Closed',    bg: 'bg-white/5',       text: 'text-[#5C7599]',  icon: X,           border: 'border-white/10' },
};

const PRIORITY_STYLE = {
  low:    { bg: 'bg-white/5',        text: 'text-[#A0B4CC]' },
  medium: { bg: 'bg-[#00E5FF]/10',   text: 'text-[#00E5FF]' },
  high:   { bg: 'bg-amber-500/10',   text: 'text-amber-400' },
  urgent: { bg: 'bg-[#FF1744]/10',   text: 'text-[#FF1744]' },
};

const TYPE_LABEL = {
  merchant_report: 'Merchant Report',
  merchant_problem: 'Merchant Problem',
  member_issue: 'Member Issue',
  other: 'Other',
};

export function AdminSupportPage() {
  const { user, api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_review: 0, resolved: 0, rejected: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const isAdmin = user?.role === 'admin';

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/admin/support/tickets?${params}`);
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error('Failed to load tickets');
    }
    setLoading(false);
  }, [api, statusFilter, typeFilter, search]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openDetail = (t) => { setSelected(t); setReply(''); };

  const refreshSelected = async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(`/api/support/tickets/${selected.id}`);
      setSelected(data);
    } catch (e) {/* skip */}
  };

  const handleReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) return;
    setPosting(true);
    try {
      await api.post(`/api/admin/support/tickets/${selected.id}/messages`, {
        content: reply || '(attached evidence)',
        attachments: replyAttachments,
      });
      setReply('');
      setReplyAttachments([]);
      await refreshSelected();
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply');
    }
    setPosting(false);
  };

  const updateTicket = async (patch) => {
    if (!isAdmin) { toast.error('Admin only'); return; }
    setSaving(true);
    try {
      await api.put(`/api/admin/support/tickets/${selected.id}`, patch);
      toast.success('Ticket updated');
      await refreshSelected();
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-support-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[#00E5FF]" /> Support Inbox
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Review and respond to disputes and support tickets</p>
          </div>
          <ExportListButtons api={api} resource="support_tickets" filename="support_tickets" />
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {['open', 'in_review', 'resolved', 'rejected', 'closed', 'all'].map(s => {
            const active = statusFilter === s;
            const st = STATUS_STYLE[s];
            return (
              <button key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                  active ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30' : 'bg-white/5 text-[#A0B4CC] border border-white/10 hover:bg-white/10'
                }`}
                data-testid={`admin-support-filter-${s}`}>
                {st && <st.icon className="w-3 h-3" />}
                {s === 'all' ? 'All' : st.label}
                {s !== 'all' && <span className="text-[10px] opacity-70">({counts[s] || 0})</span>}
              </button>
            );
          })}
        </div>

        {/* Type + search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="bg-[#041A3B] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
            data-testid="admin-support-type-filter">
            <option value="all">All types</option>
            <option value="merchant_report">Merchant Reports</option>
            <option value="merchant_problem">Merchant Problems</option>
            <option value="member_issue">Member Issues</option>
            <option value="other">Other</option>
          </select>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
            <Input placeholder="Search subject / reporter / email…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-[#041A3B] border-white/10 text-white"
              data-testid="admin-support-search" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /></div>
        ) : tickets.length === 0 ? (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-10 text-center">
              <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-[#5C7599]" />
              <p className="text-[#A0B4CC] text-sm">No tickets match this filter.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {tickets.map(t => {
              const st = STATUS_STYLE[t.status] || STATUS_STYLE.open;
              const pr = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
              const StIcon = st.icon;
              return (
                <Card key={t.id} className="bg-[#041A3B] border-white/10 hover:border-white/20 cursor-pointer"
                  onClick={() => openDetail(t)} data-testid={`admin-ticket-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-semibold text-sm">{t.subject}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text} inline-flex items-center gap-1 border ${st.border}`}>
                            <StIcon className="w-3 h-3" /> {st.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${pr.bg} ${pr.text}`}>
                            {t.priority || 'medium'}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-white/5 text-[#A0B4CC] text-[10px]">{TYPE_LABEL[t.type] || t.type}</span>
                        </div>
                        <p className="text-[#A0B4CC] text-xs mt-1">
                          From: <span className="text-white">{t.reporter_name}</span> ({t.reporter_role})
                          {t.reported_merchant_name && <> · against <span className="text-white">{t.reported_merchant_name}</span></>}
                        </p>
                        <p className="text-[#5C7599] text-[11px] mt-1">
                          Updated {new Date(t.updated_at).toLocaleString()} · {(t.messages || []).length} message{(t.messages||[]).length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail */}
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      {selected.subject}
                      <div className="text-[10px] text-[#5C7599] font-normal mt-0.5">
                        #{selected.id?.slice(0, 8)} · {TYPE_LABEL[selected.type]}
                      </div>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  {/* Meta */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded bg-[#000B18]"><span className="text-[#5C7599]">Reporter:</span> <span className="text-white">{selected.reporter_name}</span> ({selected.reporter_email})</div>
                    {selected.reported_merchant_name && (
                      <div className="p-2 rounded bg-[#000B18]"><span className="text-[#5C7599]">Against:</span> <span className="text-white">{selected.reported_merchant_name}</span></div>
                    )}
                    {selected.reason_code && (
                      <div className="p-2 rounded bg-[#000B18] col-span-2"><span className="text-[#5C7599]">Reason:</span> <span className="text-white">{selected.reason_code.replace(/_/g, ' ')}</span></div>
                    )}
                  </div>

                  {/* Admin controls */}
                  {isAdmin && (
                    <div className="p-3 rounded-lg bg-[#000B18] border border-white/5 space-y-2">
                      <p className="text-[10px] uppercase text-[#5C7599] font-medium">Manage</p>
                      <div className="flex flex-wrap gap-2">
                        <select value={selected.status}
                          onChange={e => updateTicket({ status: e.target.value })}
                          disabled={saving}
                          className="bg-[#041A3B] border border-white/20 text-white rounded-md px-2 py-1 text-xs"
                          data-testid="admin-ticket-status">
                          {['open', 'in_review', 'resolved', 'rejected', 'closed'].map(s => (
                            <option key={s} value={s}>{STATUS_STYLE[s].label}</option>
                          ))}
                        </select>
                        <select value={selected.priority || 'medium'}
                          onChange={e => updateTicket({ priority: e.target.value })}
                          disabled={saving}
                          className="bg-[#041A3B] border border-white/20 text-white rounded-md px-2 py-1 text-xs"
                          data-testid="admin-ticket-priority">
                          {['low', 'medium', 'high', 'urgent'].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Thread */}
                  <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="admin-ticket-thread">
                    {(selected.messages || []).map(m => {
                      const isStaff = m.is_staff || ['admin', 'cro', 'moderator', 'mro'].includes(m.sender_role);
                      return (
                        <div key={m.id} className={`p-3 rounded-lg ${isStaff ? 'bg-[#00E5FF]/5 border border-[#00E5FF]/20' : 'bg-[#000B18] border border-white/5'}`}>
                          <div className="flex items-center justify-between text-[10px] text-[#5C7599] mb-1">
                            <span className={isStaff ? 'text-[#00E5FF] font-medium' : 'text-white'}>
                              {m.sender_name || m.sender_role}
                              {isStaff && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#00E5FF]/10">Staff</span>}
                            </span>
                            <span>{new Date(m.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-[#A0B4CC] text-sm whitespace-pre-wrap">{m.content}</p>
                          <AttachmentGallery attachments={m.attachments} onOpen={(url) => setLightbox(url)} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply */}
                  {selected.status !== 'closed' && (
                    <div className="space-y-2">
                      <Label className="text-[#A0B4CC] text-xs">Staff reply</Label>
                      <textarea value={reply} onChange={e => setReply(e.target.value)}
                        rows={3}
                        placeholder="Reply visible to the reporter (and reported merchant if applicable)"
                        className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
                        data-testid="admin-ticket-reply" />
                      <AttachmentUploader api={api} value={replyAttachments} onChange={setReplyAttachments} testIdPrefix="admin-reply-evidence" />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  {selected.status !== 'closed' && (
                    <Button onClick={handleReply} disabled={posting || (!reply.trim() && replyAttachments.length === 0)}
                      className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                      data-testid="admin-post-reply-btn">
                      {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Reply</>}
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Lightbox for full-size attachment view */}
        {lightbox && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="admin-attachment-lightbox"
          >
            <ImageWithFallback
              src={lightbox}
              alt="Evidence"
              className="max-w-full max-h-full object-contain rounded-lg"
              fallback={
                <div className="bg-[#041A3B] border border-white/10 rounded-lg p-8 text-center text-[#A0B4CC]">
                  <p className="text-sm">This attachment cannot be displayed.</p>
                  <p className="text-xs text-[#5C7599] mt-1">The image link may have expired.</p>
                </div>
              }
            />
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
              onClick={() => setLightbox(null)}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </AdminSidebarLayout>
  );
}
